import React, { useCallback, useEffect, useMemo, useState } from "react";

import { format } from "date-fns/esm";
import { useNavigate } from "react-router-dom";
import { styled } from "styled-components";

import CategoryRequire2 from "pages/dataCollection/_shared/CategoryRequire2";
import {
  deleteShareNotice,
  fetchShareNotices,
} from "shared/api/ShareNoticeApi";
import { applyDateFilter } from "shared/lib/date/dateFilters";
import { toKstDate } from "shared/lib/date/formatKst";
import { ActionButton, EditButton } from "shared/ui/buttons";
import { alertDialog, confirmDialog } from "shared/ui/feedback";
import { SearchPanel } from "shared/ui/filters";
import { AddHeader, AlertDiv, BaseContainer } from "shared/ui/layout";
import {
  DataTable,
  Pagination,
  SelectAllToggle,
  useClientPagination,
  useTableSelection,
} from "shared/ui/table";

const ITEMS_PER_PAGE = 10;

const CONDITION_OPTIONS = ["제목만", "제목, 내용", "내용"];
const QUICK_RANGE_OPTIONS = [
  { label: "오늘", value: 0 },
  { label: "1주일", value: 1 },
  { label: "1개월", value: 2 },
  { label: "3개월", value: 3 },
  { label: "6개월", value: 4 },
  { label: "1년", value: 5 },
];

const removeFileNameExtension = (fileName) => {
  const dotIndex = fileName.indexOf(".");
  if (dotIndex !== -1) {
    return fileName.substring(dotIndex + 1);
  }
  return fileName;
};

const compareItems = (a, b) => {
  if (a.pin && !b.pin) return -1;
  if (!a.pin && b.pin) return 1;
  return new Date(b.createdAt) - new Date(a.createdAt);
};

/**
 * 자료실 공용 페이지. 카테고리 구성(config)만 받아 회칙/의결기구/예·결산/양식을
 * 모두 처리한다. 카테고리가 2개 이상이면 상단 탭(CategoryRequire2)을 노출한다.
 */
export function DataCollectionPage({ config }) {
  const navigate = useNavigate();
  const { pageName, editSlug, categories } = config;
  const hasTabs = categories.length > 1;

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [categoryTab, setCategoryTab] = useState(() =>
    categories.map((_, index) => index === 0),
  );

  const activeCategoryId = useMemo(() => {
    const activeIndex = categoryTab.findIndex(Boolean);
    return categories[activeIndex === -1 ? 0 : activeIndex].id;
  }, [categoryTab, categories]);

  const getCategoryName = useCallback(
    (categoryId) =>
      categories.find((category) => category.id === categoryId)?.label ?? "",
    [categories],
  );

  const readDataNotice = useCallback(async () => {
    try {
      // 활성 탭의 단일 category만 서버에서 조회한다(전량 로딩 제거).
      const notices = await fetchShareNotices(activeCategoryId);

      // 안전망: 백엔드 category 필터 미배포 대비 클라이언트에서도 단일 category만
      // 남긴다. (백엔드 배포 확인 후 제거 가능)
      setData(notices.filter((item) => item.category === activeCategoryId));
    } catch (error) {
      // console.error("Error fetching notices:", error);
    }
  }, [activeCategoryId]);

  useEffect(() => {
    readDataNotice();
  }, [readDataNotice]);

  // 카테고리 탭(또는 데이터)이 바뀌면 해당 카테고리만 노출
  useEffect(() => {
    setFilteredData(data.filter((item) => item.category === activeCategoryId));
  }, [data, activeCategoryId]);

  const handleSearchFilters = (filters) => {
    const { condition, dateButtonClicked, startDate, endDate, keyword } =
      filters;

    // 현재 선택된 기구(카테고리) 탭의 데이터에 대해서만 검색한다.
    let filteredResults = data.filter(
      (item) => item.category === activeCategoryId,
    );
    const keywordLower = keyword.toLowerCase();

    if (condition === "제목만") {
      filteredResults = filteredResults.filter((item) =>
        item.title?.toLowerCase().includes(keywordLower),
      );
    } else if (condition === "제목, 내용") {
      filteredResults = filteredResults.filter(
        (item) =>
          item.title?.toLowerCase().includes(keywordLower) ||
          item.content?.toLowerCase().includes(keywordLower),
      );
    } else if (condition === "내용") {
      filteredResults = filteredResults.filter((item) =>
        item.content?.toLowerCase().includes(keywordLower),
      );
    }

    filteredResults = applyDateFilter({
      data: filteredResults,
      dateButtonClicked,
      startDate,
      endDate,
      // createdAt은 서버가 UTC로 생성하므로 KST 보정해서 비교.
      getDate: (item) => toKstDate(item.createdAt) ?? new Date(NaN),
    });

    setFilteredData(filteredResults);
  };

  const handleRegisterClick = () => {
    navigate(`/dataCollection/register/${activeCategoryId}`);
  };

  const handleEditClick = useCallback(
    (dataNoticeId) => {
      navigate(`/dataCollection/${editSlug}/${dataNoticeId}`);
    },
    [navigate, editSlug],
  );

  // "[오늘 등록된 새 글 n건]" 카운트
  // createdAt은 서버가 UTC로 생성하므로 KST 보정 후 오늘 여부 비교.
  const newPostsCount = useMemo(() => {
    const today = new Date();
    return data.filter((item) => {
      const postDate = toKstDate(item.createdAt);
      return (
        postDate &&
        postDate.getDate() === today.getDate() &&
        postDate.getMonth() === today.getMonth() &&
        postDate.getFullYear() === today.getFullYear()
      );
    }).length;
  }, [data]);

  const columns = useMemo(
    () => [
      { Header: "No.", accessor: "id" },
      {
        Header: "제목",
        accessor: "title",
        Cell: ({ row }) => (
          <TitleCell>
            {row.original.pin ? (
              <svg
                width="10"
                height="17"
                viewBox="0 0 10 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ marginRight: "10px", flexShrink: 0 }}
                role="img"
                aria-label="고정"
              >
                <path
                  d="M8 8.66602V2.26602H8.8V0.666016H0.8V2.26602H1.6V8.66602L0 10.266V11.866H4.16V16.666H5.44V11.866H9.6V10.266L8 8.66602Z"
                  fill="#0B589A"
                />
              </svg>
            ) : (
              ""
            )}
            {row.original.title}
          </TitleCell>
        ),
      },
      { Header: "카테고리", accessor: "category" },
      {
        Header: "파일",
        accessor: "files",
        Cell: ({ value }) => {
          const files = value ?? [];
          return (
            <FileList>
              {files.map((file, index) => (
                <FileLink
                  key={`file_${file.link ?? file.title ?? index}`}
                  href={file.link}
                  download
                  title={removeFileNameExtension(file.title)}
                >
                  {removeFileNameExtension(file.title)}
                </FileLink>
              ))}
            </FileList>
          );
        },
      },
      {
        Header: "게시일",
        accessor: "createdAt",
        Cell: ({ value }) => {
          // createdAt은 서버가 UTC로 생성하므로 KST 보정해서 표시한다.
          const kstDate = toKstDate(value);
          return kstDate ? format(kstDate, "yyyy-MM-dd HH:mm:ss") : "-";
        },
      },
      { Header: "조회수", accessor: "viewCnt" },
      {
        Header: "수정",
        accessor: "edit",
        Cell: ({ row }) => (
          <EditBtn onClick={() => handleEditClick(row.original.id)}>수정</EditBtn>
        ),
      },
    ],
    [handleEditClick],
  );

  const sortedData = [...filteredData].sort(compareItems);
  const selection = useTableSelection(sortedData);
  const { pageItems, page, setPage, totalCount } = useClientPagination(
    sortedData,
    { pageSize: ITEMS_PER_PAGE },
  );
  const tableData = pageItems.map((item) => ({
    ...item,
    category: getCategoryName(item.category),
  }));

  // 초기화: 검색 조건을 비우고 현재 탭의 전체 데이터를 다시 노출한다.
  const handleResetFilters = () => {
    setFilteredData(data.filter((item) => item.category === activeCategoryId));
    setPage(1);
  };

  const handleDeleteClick = async () => {
    if (selection.count === 0) {
      alertDialog("선택된 항목이 없습니다.");
      return;
    }

    const selectedRowsIds = selection.selectedRows.map((row) => row.id);
    const confirmDelete = await confirmDialog("선택한 항목을 삭제하시겠습니까?");
    if (!confirmDelete) return;

    try {
      await Promise.all(selectedRowsIds.map((id) => deleteShareNotice(id)));

      selection.clear();
      await readDataNotice();
      alertDialog("삭제되었습니다.");
    } catch (error) {
      // console.error("Error deleting rows:", error);
    }
  };

  return (
    <BaseContainer>
      <AddHeader
        pageName={pageName}
        onClick={handleRegisterClick}
        buttonValue="자료 등록하기"
      />
      <SearchPanel onSearch={handleSearchFilters} onReset={handleResetFilters}>
        <SearchPanel.Keyword label="조건 검색" dropdown={CONDITION_OPTIONS} />
        <SearchPanel.QuickRange label="게시 기간" options={QUICK_RANGE_OPTIONS} />
        <SearchPanel.DateRange />
      </SearchPanel>
      {hasTabs && (
        <CategoryRequire2
          dateButtonClicked={categoryTab}
          setDateButtonClicked={setCategoryTab}
          setPage={setPage}
          category={categories.map((category) => category.label)}
        />
      )}
      <AlertDiv>
        <p>
          [오늘 등록된 새 글 <P>{newPostsCount}</P>건] 검색 결과{" "}
          <P>{filteredData.length}</P>건 검색 되었습니다.
        </p>
        <ActionGroup>
          <SelectAllToggle
            totalCount={sortedData.length}
            selectedCount={selection.count}
            allSelected={selection.isAllSelected}
            onToggle={() =>
              selection.isAllSelected ? selection.clear() : selection.selectAll()
            }
          />
          <ActionButton onClick={handleDeleteClick} disabled={selection.count === 0}>
            삭제
          </ActionButton>
        </ActionGroup>
      </AlertDiv>
      <DataDiv>
        <DataTable
          columns={columns}
          data={tableData}
          selectable
          selection={selection}
        />
      </DataDiv>
      <Pagination
        page={page}
        pageSize={ITEMS_PER_PAGE}
        totalCount={totalCount}
        onPageChange={setPage}
      />
    </BaseContainer>
  );
}

export default DataCollectionPage;

const DataDiv = styled.div`
  width: 100%;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
`;


const EditBtn = styled(EditButton)`
  width: fit-content;
  height: fit-content;
  padding: 7px 23px;
  letter-spacing: 0em;
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const FileLink = styled.a`
  display: block;
  max-width: 360px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #0079e0;
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    text-decoration: none;
  }
`;

const P = styled.span`
  color: #0079e0;
  font-weight: bold;
`;

const TitleCell = styled.div`
  display: block;
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-start;
  text-decoration: underline;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
